import { css } from "styled-components"

import { spacing, Spacing, colors, borderRadius } from "@izettle/otto"

export type ActionColorStateKeys = keyof typeof colors.action.primary.text
export type ActionColorKeys = keyof typeof colors.action
export type ActionColors = typeof colors.action[ActionColorKeys]

export const getActionStateColors = (
  actionColorKey: ActionColorKeys,
  state: ActionColorStateKeys,
) => css`
  border-color: ${colors.action[actionColorKey].border[state]};
  background: ${colors.action[actionColorKey].background[state]};
  color: ${colors.action[actionColorKey].text[state]};
`

export const getActionColors = (actionColorKey: ActionColorKeys) => css`
  ${getActionStateColors(actionColorKey, "default")};
  &:hover {
    ${getActionStateColors(actionColorKey, "hover")};
  }
  &:active {
    ${getActionStateColors(actionColorKey, "selected")};
  }
  &:disabled {
    ${getActionStateColors(actionColorKey, "disabled")};
  }
`

export const resetFocusMixin = css`
  outline: none;
  &::-moz-focus-inner {
    border: 0;
  }
`

export const focusOutlineMixin = css`
  outline: ${colors.border.focusForm} solid 1px;
`

export const getHorizontalPaddingAndBorderWidth =
  (space: Spacing) => (borderWidth: string) =>
    css`
      padding: 0 calc(${spacing[space]} - ${borderWidth});
      border-width: ${borderWidth};
    `

const ACTION_FOCUS_COLOR_VAR = "--action-focus-color"

/**
 * Use together with `actionFocusMixin` to edit the css variable used for the color of
 * the focus ring. Only use within the scope of a specific component, or it will impact
 * the focus ring color of the whole page.
 */
export const setActionFocusColorMixin = (color: string) =>
  `${ACTION_FOCUS_COLOR_VAR}: ${color}`

/**
 * Replaces the default focus styles of the browser with a nice focus ring. Use within the
 * `:focus` selector of your components styles.
 *
 * Uses the `::before` selector to insert the focus ring, so make sure that the component is
 * not already using the same selector for something diferent.
 *
 * Use the `MouseClickFocusRemover` component inside the component to only apply the focus
 * styles on keyboard navigation.
 *
 * Edit the color of the focus ring using the `setActionFocusColorMixin` within the styles
 * of your component.
 */
export const actionFocusMixin = css`
  ${resetFocusMixin}

  &:before {
    content: "";
    pointer-events: none;
    box-sizing: border-box;
    position: absolute;
    border: 1px solid var(${ACTION_FOCUS_COLOR_VAR}, ${colors.border.focusForm});
    height: calc(100% + 8px);
    width: calc(100% + 8px);
    top: -4px;
    left: -4px;
    border-radius: ${borderRadius.large};
    z-index: 1;
  }
`

export type Margins = {
  marginTop?: Spacing
  marginBottom?: Spacing
}

type DefaultMargins = {
  top?: Spacing
  bottom?: Spacing
}

// prettier-ignore
export const marginTopBottomMixin = ({ top, bottom }: DefaultMargins) => css<Margins>`
  ${({ marginTop = top }) => marginTop ? `margin-top: ${spacing[marginTop]};` : ""}
  ${({ marginBottom = bottom }) => marginBottom ? `margin-bottom: ${spacing[marginBottom]};` : ""}
`
